/**
 * The five lengths a hire may be sold for (spec §11 · I4 · decision #15).
 *
 * > Ceny: odgórne, stałe, progi: 1 dzień, 3 dni, 7 dni, 14 dni, 30 dni.
 * > Inne długości niedozwolone.
 *
 * It lives in `@repo/db` rather than in `@repo/core` for one reason: the SEED
 * writes these variants, and `@repo/db` may not import `@repo/core` — the
 * dependency runs the other way. Putting the table beside the seed that
 * consumes it, and letting `@repo/core` import it from here, keeps ONE list of
 * thresholds; a second copy in the domain layer would be two lists that agree
 * until the day somebody edits one.
 *
 * `@repo/core/offering/rental-terms.ts` is where the checkout-facing rules sit
 * (which lengths are refused, what the refusal says). Only the catalogue shape
 * is here.
 *
 * Pure and dependency-free.
 */

/** Spec §11's thresholds, in days, ascending. */
export const RENTAL_TERMS = [1, 3, 7, 14, 30] as const
export type RentalTerm = (typeof RENTAL_TERMS)[number]

/**
 * What each threshold takes off the daily rate × its length (decision #15).
 *
 * TODO(flou): ORIENTATION values. They reproduce the `RENTAL_DAYS` ladder the
 * site applied before I4, so nothing anybody was quoted moves — they are not a
 * rate card anybody has signed off. Replace them in the panel, per item.
 */
export const RENTAL_TERM_DISCOUNT_BPS: Record<RentalTerm, number> = {
  1: 0,
  3: 1000,
  7: 2500,
  14: 3000,
  30: 3500,
}

/** The variant slug a threshold is sold under. `doba` is kept for the 1-day row. */
export function termSlug(days: RentalTerm): string {
  return days === 1 ? 'doba' : `${String(days)}-dni`
}

/** …and back. `null` for a variant that is not one of the five. */
export function termOfSlug(slug: string): RentalTerm | null {
  return RENTAL_TERMS.find((days) => termSlug(days) === slug) ?? null
}

/** "Doba", "3 doby", "14 dób" — Polish counts, not "1 dzień". */
export function termLabel(days: RentalTerm): string {
  if (days === 1) return 'Doba'
  const last = days % 10
  const word = last >= 2 && last <= 4 && (days < 10 || days > 20) ? 'doby' : 'dób'
  return `${String(days)} ${word}`
}

export function isRentalTerm(days: number): days is RentalTerm {
  return (RENTAL_TERMS as readonly number[]).includes(days)
}

/**
 * What a threshold costs, derived from the daily rate (decision #15).
 *
 * Rounded to the grosz — the house policy since H2's decision #7. The rounding
 * mode is not a parameter here because the catalogue stores ONE frozen price
 * per threshold; the policy applies to what a basket does with it afterwards.
 */
export function termPriceMinor(dailyRateMinor: number, days: RentalTerm): number {
  const base = Math.max(0, dailyRateMinor) * days
  return Math.round((base * (10_000 - RENTAL_TERM_DISCOUNT_BPS[days])) / 10_000)
}

/**
 * {@link termPriceMinor} read backwards: what one DAY costs, given what a
 * threshold costs.
 *
 * The five rungs were generated once, at seed time, and then drifted — editing
 * „Doba" in the panel moved one number and left the other four quoting a ladder
 * that came off nothing. This is the half that lets a rung say what a day is
 * worth, so the panel can spread one edit across the ladder („niezależnie czy
 * zmienię 3, 7, 14 dobę reszta zmienia się automatycznie").
 *
 * NOT an exact inverse, and it cannot be: `termPriceMinor` rounds to the grosz,
 * so several daily rates produce the same threshold price and only one of them
 * comes back. The round trip is stable to within a grosz per day, which is the
 * most a rounded figure can promise — and for the 1-day rung, where the
 * discount is zero, it is exact.
 */
export function dailyFromTermPrice(termNetMinor: number, days: RentalTerm): number {
  const discounted = days * (10_000 - RENTAL_TERM_DISCOUNT_BPS[days])
  return Math.round((Math.max(0, termNetMinor) * 10_000) / discounted)
}
